import React from 'react';
import { client } from 'src/utils/api-client';
import {
  EcoinventActivity,
  EcoinventGroupedActivity,
  EcoinventRecentActivity,
  EcoinventRecommendedActivities,
  LCAObject,
} from '../index.types';
import {
  Autocomplete,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { StyledTextField } from '../Modals/index.styles';
import Button from 'src/Lca/components/Button';
import { useTranslation } from 'react-i18next';

const MIN_SYMBOL_LENGTH = 4;
const SEARCH_DEBOUNCE_MS = 300;
const OPEN_ECOINVENT_BUTTON_WIDTH = 240;

type ActivityOptionGroup = 'recommended_subcategory' | 'search';
type ActivityOption = EcoinventGroupedActivity & {
  option_group: ActivityOptionGroup;
};

const getGroupedActivityKey = (
  activity: Pick<
    EcoinventGroupedActivity,
    'activity_name' | 'reference_product_name' | 'reference_product_unit'
  >
) =>
  `${activity.activity_name}::${activity.reference_product_name}::${activity.reference_product_unit}`;

const getGeographyOptionLabel = (
  activity: Pick<EcoinventActivity, 'geography' | 'geography_display'>
) => {
  const geographyCode = activity.geography;
  const geographyDisplay = activity.geography_display?.trim();
  if (!geographyDisplay || geographyDisplay === geographyCode) {
    return geographyCode;
  }
  return `${geographyDisplay} (${geographyCode})`;
};

const getOptionPrimaryLabel = (
  option: Pick<EcoinventActivity, 'activity_name' | 'reference_product_name'>
) => `${option.activity_name} - ${option.reference_product_name}`;

interface Props {
  value: number | null;
  object?: LCAObject;
  // eslint-disable-next-line no-unused-vars
  onChange: (object: EcoinventActivity | null) => void;
  ecoinventVersion?: string;
  productId?: number;
  subcategoryId?: number | null;
  isEcoinventLicenceValid: boolean;
  preferredGeography?: string;
}

export default function ActivitiesSearchField({
  object = undefined,
  value,
  onChange,
  ecoinventVersion,
  productId,
  subcategoryId,
  isEcoinventLicenceValid,
  preferredGeography,
}: Props) {
  const { t } = useTranslation('lca');
  const [geographyOptions, setGeographyOptions] = React.useState<
    EcoinventActivity[]
  >([]);
  const [searchInput, setSearchInput] = React.useState('');
  const [isGroupedLoading, setIsGroupedLoading] = React.useState(false);
  const [isRecommendedLoading, setIsRecommendedLoading] = React.useState(false);
  const [isGeographiesLoading, setIsGeographiesLoading] = React.useState(false);
  const [recentCategoryActivities, setRecentCategoryActivities] =
    React.useState<EcoinventRecentActivity[]>([]);
  const [searchedActivities, setSearchedActivities] = React.useState<
    EcoinventGroupedActivity[]
  >([]);
  const [selectedGroupedActivity, setSelectedGroupedActivity] =
    React.useState<ActivityOption | null>(null);
  const [selectedGeographyActivityId, setSelectedGeographyActivityId] =
    React.useState<number | ''>('');
  const [isUsedBeforeModalOpen, setIsUsedBeforeModalOpen] =
    React.useState(false);

  const recommendationsRequestIdRef = React.useRef(0);
  const groupedRequestIdRef = React.useRef(0);
  const geographiesRequestIdRef = React.useRef(0);
  const searchTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const objectActivity = object?.ecoinvent_activity;
  const isSearchActive = searchInput.trim().length >= MIN_SYMBOL_LENGTH;

  const toGroupedActivity = React.useCallback(
    (activity: EcoinventActivity): ActivityOption => ({
      id: activity.id,
      activity_name: activity.activity_name,
      reference_product_name: activity.reference_product_name,
      reference_product_unit: activity.reference_product_unit,
      geographies_count: 1,
      label: `${activity.activity_name} - ${activity.reference_product_name}`,
      option_group: 'recommended_subcategory',
    }),
    []
  );

  const activityOptions = React.useMemo<ActivityOption[]>(() => {
    const uniqueOptionsByKey = new Map<string, ActivityOption>();

    const addOption = (activity: EcoinventGroupedActivity) => {
      const key = getGroupedActivityKey(activity);
      if (!uniqueOptionsByKey.has(key)) {
        uniqueOptionsByKey.set(key, { ...activity, option_group: 'search' });
      }
    };

    if (isSearchActive) {
      for (const activity of searchedActivities) {
        addOption(activity);
      }
    }

    const options = Array.from(uniqueOptionsByKey.values());
    if (!selectedGroupedActivity) return options;

    const selectedKey = getGroupedActivityKey(selectedGroupedActivity);
    if (
      options.some((option) => getGroupedActivityKey(option) === selectedKey)
    ) {
      return options;
    }

    return [
      {
        ...selectedGroupedActivity,
        option_group: 'search' as ActivityOptionGroup,
      },
      ...options,
    ];
  }, [isSearchActive, searchedActivities, selectedGroupedActivity]);

  const getGroupedActivities = React.useCallback(() => {
    if (!ecoinventVersion) {
      setIsGroupedLoading(false);
      setSearchedActivities([]);
      return;
    }
    const searchText = searchInput.trim();
    if (searchText.length < MIN_SYMBOL_LENGTH) {
      setIsGroupedLoading(false);
      setSearchedActivities([]);
      return;
    }

    const params = new URLSearchParams({
      symbol: searchText,
      limit: '50',
      version: ecoinventVersion,
    });

    const requestId = groupedRequestIdRef.current + 1;
    groupedRequestIdRef.current = requestId;
    setIsGroupedLoading(true);

    client
      .get<EcoinventGroupedActivity[]>(
        `/web/lca/activities/grouped?${params.toString()}`
      )
      .then((res) => {
        if (groupedRequestIdRef.current !== requestId) return;
        setSearchedActivities(res.data);
      })
      .catch(() => {
        if (groupedRequestIdRef.current !== requestId) return;
        setSearchedActivities([]);
      })
      .finally(() => {
        if (groupedRequestIdRef.current !== requestId) return;
        setIsGroupedLoading(false);
      });
  }, [ecoinventVersion, searchInput]);

  React.useEffect(() => {
    if (
      !isEcoinventLicenceValid ||
      !productId ||
      !ecoinventVersion ||
      subcategoryId === null ||
      subcategoryId === undefined
    ) {
      recommendationsRequestIdRef.current += 1;
      setRecentCategoryActivities([]);
      setIsRecommendedLoading(false);
      return;
    }

    const params = new URLSearchParams({
      product_id: String(productId),
      limit: '100',
      subcategory_id: String(subcategoryId),
    });

    const requestId = recommendationsRequestIdRef.current + 1;
    recommendationsRequestIdRef.current = requestId;
    setIsRecommendedLoading(true);

    client
      .get<EcoinventRecommendedActivities>(
        `/web/lca/activities/recommended?${params.toString()}`
      )
      .then((response) => {
        if (recommendationsRequestIdRef.current !== requestId) return;
        setRecentCategoryActivities(
          response.data.recent_category_activities || []
        );
      })
      .catch(() => {
        if (recommendationsRequestIdRef.current !== requestId) return;
        setRecentCategoryActivities([]);
      })
      .finally(() => {
        if (recommendationsRequestIdRef.current !== requestId) return;
        setIsRecommendedLoading(false);
      });
  }, [ecoinventVersion, isEcoinventLicenceValid, productId, subcategoryId]);

  React.useEffect(() => {
    if (!objectActivity) return;
    const grouped = toGroupedActivity(objectActivity);
    setSelectedGroupedActivity(grouped);
    setSelectedGeographyActivityId(objectActivity.id);
  }, [objectActivity, toGroupedActivity]);

  React.useEffect(() => {
    if (!isEcoinventLicenceValid || !ecoinventVersion) return undefined;
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      getGroupedActivities();
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [ecoinventVersion, getGroupedActivities, isEcoinventLicenceValid]);

  React.useEffect(() => {
    if (!ecoinventVersion || !selectedGroupedActivity) {
      setGeographyOptions([]);
      setIsGeographiesLoading(false);
      return;
    }

    const params = new URLSearchParams({
      activity_id: String(selectedGroupedActivity.id),
      version: ecoinventVersion,
    });
    if (preferredGeography) {
      params.set('preferred_geography', preferredGeography);
    }

    const requestId = geographiesRequestIdRef.current + 1;
    geographiesRequestIdRef.current = requestId;
    setIsGeographiesLoading(true);

    client
      .get<EcoinventActivity[]>(
        `/web/lca/activities/grouped/geographies?${params.toString()}`
      )
      .then((response) => {
        if (geographiesRequestIdRef.current !== requestId) return;
        const items = response.data;
        setGeographyOptions(items);
        if (
          selectedGeographyActivityId &&
          items.some((item) => item.id === selectedGeographyActivityId)
        ) {
          return;
        }
        if (
          objectActivity &&
          items.some((item) => item.id === objectActivity.id)
        ) {
          const matchingObjectActivity = items.find(
            (item) => item.id === objectActivity.id
          );
          setSelectedGeographyActivityId(objectActivity.id);
          onChange(matchingObjectActivity ?? objectActivity);
          return;
        }
        setSelectedGeographyActivityId('');
      })
      .catch(() => {
        if (geographiesRequestIdRef.current !== requestId) return;
        setGeographyOptions([]);
        setSelectedGeographyActivityId('');
      })
      .finally(() => {
        if (geographiesRequestIdRef.current !== requestId) return;
        setIsGeographiesLoading(false);
      });
  }, [
    ecoinventVersion,
    objectActivity,
    onChange,
    preferredGeography,
    selectedGroupedActivity,
  ]);

  const handleGroupedActivityChange = (
    _: React.SyntheticEvent<Element, Event>,
    option: ActivityOption | null
  ) => {
    const hasChanged =
      (option ? getGroupedActivityKey(option) : null) !==
      (selectedGroupedActivity
        ? getGroupedActivityKey(selectedGroupedActivity)
        : null);
    setSelectedGroupedActivity(option);
    setSearchInput(option ? getOptionPrimaryLabel(option) : '');
    if (hasChanged) {
      setSelectedGeographyActivityId('');
      setGeographyOptions([]);
      onChange(null);
    }
  };

  const handleGeographyChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const activityIdValue = event.target.value;
    if (!activityIdValue) {
      setSelectedGeographyActivityId('');
      onChange(null);
      return;
    }

    const selectedId = Number(activityIdValue);
    setSelectedGeographyActivityId(selectedId);
    const selectedActivity = geographyOptions.find(
      (item) => item.id === selectedId
    );
    onChange(selectedActivity ?? null);
  };

  const handleRecentQuickPick = (recentActivity: EcoinventRecentActivity) => {
    const groupedOption: ActivityOption = {
      id: recentActivity.id,
      activity_name: recentActivity.activity_name,
      reference_product_name: recentActivity.reference_product_name,
      reference_product_unit: recentActivity.reference_product_unit,
      geographies_count: 1,
      label: getOptionPrimaryLabel(recentActivity),
      option_group: 'recommended_subcategory',
    };
    setSelectedGroupedActivity(groupedOption);
    setSearchInput(getOptionPrimaryLabel(groupedOption));
    setSelectedGeographyActivityId(recentActivity.id);
    setGeographyOptions((prev) => {
      if (prev.some((item) => item.id === recentActivity.id)) return prev;
      return [recentActivity, ...prev];
    });
    onChange(recentActivity);
  };
  const handleUsedBeforeActivitySelect = (
    recentActivity: EcoinventRecentActivity
  ) => {
    handleRecentQuickPick(recentActivity);
    setIsUsedBeforeModalOpen(false);
  };

  const quickPickActivities = React.useMemo(
    () =>
      recentCategoryActivities.filter(
        (activity, index, allActivities) =>
          allActivities.findIndex((item) => item.id === activity.id) === index
      ),
    [recentCategoryActivities]
  );

  const noOptionsText = !isSearchActive
    ? t('ecoinventSearchMinChars', { count: MIN_SYMBOL_LENGTH })
    : t('ecoinventSearchNoResults');
  const hasEcoinventLibrary = !!ecoinventVersion;
  const selectedGeographyValue = geographyOptions.some(
    (activity) => activity.id === selectedGeographyActivityId
  )
    ? selectedGeographyActivityId
    : '';
  const controlsGridColumns = hasEcoinventLibrary
    ? `minmax(0, 1fr) auto ${OPEN_ECOINVENT_BUTTON_WIDTH}px`
    : 'minmax(0, 1fr) auto';

  return (
    <Box
      sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1 }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: controlsGridColumns,
          columnGap: 1,
          rowGap: 1,
          alignItems: 'center',
        }}
      >
        <Box sx={{ minWidth: 0, gridColumn: 1 }}>
          <Autocomplete<ActivityOption, false, false, false>
            fullWidth
            options={activityOptions}
            value={selectedGroupedActivity}
            onChange={handleGroupedActivityChange}
            inputValue={searchInput}
            onInputChange={(_, newInputValue) => {
              setSearchInput(newInputValue);
            }}
            sx={{ backgroundColor: value ? '#F6F6F6' : 'inherit' }}
            renderInput={(params) => (
              <StyledTextField
                {...params}
                value={searchInput}
                label={t('ecoinventSearch')}
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box
                  sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.25,
                    py: 0.25,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {getOptionPrimaryLabel(option)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 1.5,
                      flexWrap: 'wrap',
                      color: 'text.secondary',
                    }}
                  >
                    <Typography variant="caption">
                      {t('ecoinventSearchUnitLabel', {
                        unit: option.reference_product_unit,
                      })}
                    </Typography>
                    <Typography variant="caption">
                      {t('ecoinventSearchGeographiesLabel', {
                        count: option.geographies_count,
                      })}
                    </Typography>
                    {!!option.usage_count && (
                      <Typography variant="caption">
                        {t('ecoinventSearchUsedCount', {
                          count: option.usage_count,
                        })}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </li>
            )}
            getOptionLabel={(option) => option.label ?? ''}
            isOptionEqualToValue={(option, selected) =>
              getGroupedActivityKey(option) === getGroupedActivityKey(selected)
            }
            filterOptions={(items) => items}
            noOptionsText={noOptionsText}
            loading={isGroupedLoading || isRecommendedLoading}
            disabled={!isEcoinventLicenceValid}
          />
        </Box>
        <Tooltip title={t('ecoinventUsedBeforeTooltip')}>
          <IconButton
            onClick={() => setIsUsedBeforeModalOpen(true)}
            size="small"
            sx={{ gridColumn: 2 }}
          >
            <HistoryIcon />
          </IconButton>
        </Tooltip>
        {hasEcoinventLibrary && (
          <Box sx={{ gridColumn: 3 }}>
            <Button
              color="default"
              onClick={() =>
                window.open(
                  `https://ecoquery.ecoinvent.org/${ecoinventVersion}/cutoff/search`,
                  '_blank'
                )
              }
              isSecondary
              style={{
                width: OPEN_ECOINVENT_BUTTON_WIDTH,
                justifyContent: 'center',
                padding: '10px 20px',
              }}
            >
              {t('diagram.openEcoinvent')}
            </Button>
          </Box>
        )}

        {selectedGroupedActivity && (
          <Box sx={{ display: 'flex', gap: 1, gridColumn: 1 }}>
            <StyledTextField
              select
              fullWidth
              label={t('ecoinventSearchGeography')}
              value={selectedGeographyValue}
              onChange={handleGeographyChange}
              disabled={!isEcoinventLicenceValid || isGeographiesLoading}
            >
              <MenuItem value="">
                {t('ecoinventSearchSelectGeography')}
              </MenuItem>
              {geographyOptions.map((activity) => (
                <MenuItem key={activity.id} value={activity.id}>
                  {getGeographyOptionLabel(activity)}
                </MenuItem>
              ))}
            </StyledTextField>
          </Box>
        )}
      </Box>

      <Dialog
        open={isUsedBeforeModalOpen}
        onClose={() => setIsUsedBeforeModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t('ecoinventQuickPicks')}</DialogTitle>
        <DialogContent dividers>
          {quickPickActivities.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('ecoinventUsedBeforeModalEmpty')}
            </Typography>
          ) : (
            <List sx={{ py: 0 }}>
              {quickPickActivities.map((activity) => (
                <ListItemButton
                  key={`used-before-modal-${activity.id}`}
                  onClick={() => handleUsedBeforeActivitySelect(activity)}
                  sx={{
                    borderRadius: 1,
                    mb: 0.25,
                    alignItems: 'flex-start',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                  }}
                >
                  <ListItemText
                    primary={getOptionPrimaryLabel(activity)}
                    secondary={getGeographyOptionLabel(activity)}
                    primaryTypographyProps={{
                      variant: 'body2',
                      sx: { fontWeight: 500 },
                    }}
                    secondaryTypographyProps={{
                      variant: 'caption',
                      sx: { color: 'text.secondary' },
                    }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
